import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { type AgentBuilderState } from '../AgentBuilder'
import { type AgentType } from '@/lib/api/services/aiStudio'

interface BasicInfoStepProps {
  state: AgentBuilderState
  updateState: (updates: Partial<AgentBuilderState>) => void
  errors: Record<string, string>
}

const EMOJIS = ['🤖', '🧠', '✨', '📝', '💡', '🎯', '🔮', '🦾', '🎨', '📊', '💬', '🔍']

const COLORS = [
  { id: 'purple', bg: 'bg-purple-500', name: 'Purple' },
  { id: 'blue', bg: 'bg-blue-500', name: 'Blue' },
  { id: 'emerald', bg: 'bg-emerald-500', name: 'Emerald' },
  { id: 'amber', bg: 'bg-amber-500', name: 'Amber' },
  { id: 'rose', bg: 'bg-rose-500', name: 'Rose' },
  { id: 'cyan', bg: 'bg-cyan-500', name: 'Cyan' },
]

const AGENT_TYPES: { value: AgentType; label: string; description: string }[] = [
  { value: 'General', label: 'General Purpose', description: 'Versatile agent for various tasks' },
  { value: 'Writer', label: 'Writer', description: 'Specialized in content creation' },
  { value: 'Analyst', label: 'Analyst', description: 'Data analysis and insights' },
  { value: 'Assistant', label: 'Assistant', description: 'Personal productivity helper' },
  { value: 'Custom', label: 'Custom', description: 'Fully customized configuration' },
]

export function BasicInfoStep({ state, updateState, errors }: BasicInfoStepProps) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Basic Information</h2>
        <p className="text-gray-500 dark:text-gray-400">
          Give your agent a name and description to help users understand its purpose.
        </p>
      </div>

      {/* Emoji and Color Selection */}
      <div className="flex items-start gap-6">
        <div className="space-y-2">
          <Label className="text-gray-700 dark:text-gray-300">Icon</Label>
          <div className="flex gap-2">
            {EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => updateState({ emoji })}
                className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center transition-all ${
                  state.emoji === emoji
                    ? 'bg-purple-100 dark:bg-purple-900/30 ring-2 ring-purple-500'
                    : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-700 dark:text-gray-300">Color</Label>
          <div className="flex gap-2">
            {COLORS.map((color) => (
              <button
                key={color.id}
                onClick={() => updateState({ color: color.id })}
                className={`w-10 h-10 rounded-xl ${color.bg} transition-all ${
                  state.color === color.id
                    ? 'ring-2 ring-offset-2 ring-gray-900 dark:ring-white'
                    : 'opacity-70 hover:opacity-100'
                }`}
                title={color.name}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="name" className="text-gray-700 dark:text-gray-300">
          Agent Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="name"
          value={state.name}
          onChange={(e) => updateState({ name: e.target.value })}
          placeholder="e.g., Content Writer Pro"
          className={`rounded-xl ${errors.name ? 'border-red-500' : ''}`}
          maxLength={50}
        />
        {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
        <p className="text-xs text-gray-500">{state.name.length}/50 characters</p>
      </div>

      {/* Slug Preview */}
      {state.slug && (
        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            URL: <code className="text-purple-600 dark:text-purple-400">/agents/{state.slug}</code>
          </p>
        </div>
      )}

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description" className="text-gray-700 dark:text-gray-300">
          Description
        </Label>
        <Textarea
          id="description"
          value={state.description}
          onChange={(e) => updateState({ description: e.target.value })}
          placeholder="Describe what this agent does and how it can help..."
          className="rounded-xl resize-none"
          rows={3}
          maxLength={500}
        />
        <p className="text-xs text-gray-500">{state.description.length}/500 characters</p>
      </div>

      {/* Agent Type */}
      <div className="space-y-2">
        <Label className="text-gray-700 dark:text-gray-300">Agent Type</Label>
        <Select
          value={state.agentType}
          onValueChange={(value: AgentType) => updateState({ agentType: value })}
        >
          <SelectTrigger className="rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AGENT_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                <div>
                  <p className="font-medium">{type.label}</p>
                  <p className="text-xs text-gray-500">{type.description}</p>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Preview */}
      <div className="p-6 rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Preview</p>
        <div className="flex items-center gap-4">
          <div
            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl ${
              state.color === 'purple' ? 'bg-purple-500' :
              state.color === 'blue' ? 'bg-blue-500' :
              state.color === 'emerald' ? 'bg-emerald-500' :
              state.color === 'amber' ? 'bg-amber-500' :
              state.color === 'rose' ? 'bg-rose-500' :
              'bg-cyan-500'
            }`}
          >
            {state.emoji}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {state.name || 'Agent Name'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {state.description || 'Agent description will appear here'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
